import { clamp, isNil } from 'es-toolkit'
import { chunk, random, times } from 'es-toolkit/compat'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import type { File as UploadedFile } from '@/types/File'
import type { UUID } from '@/types/Id'

export type UploadUrlOut = {
  s3_key: string
  url: string
  headers: Record<string, string>
}

export type CompleteUploadBatchErrorStage = 'create' | 'post_create' | 'enqueue'

export interface CompleteUploadBatchError {
  s3_key: string
  stage: CompleteUploadBatchErrorStage
  message: string
  file_created: boolean
}

export interface CompleteUploadBatchResponse {
  created: UploadedFile[]
  errors: CompleteUploadBatchError[]
}

export interface UploadFilesPresignedFailure {
  file: File
  stage: 'upload' | 'complete'
  message: string
  s3Key?: string
  fileCreated: boolean
}

export interface UploadFilesPresignedResult {
  created: UploadedFile[]
  failed: UploadFilesPresignedFailure[]
}

export class PartialUploadError extends Error {
  public readonly result: UploadFilesPresignedResult

  constructor(result: UploadFilesPresignedResult) {
    super(
      `${result.failed.length} of ${result.created.length + result.failed.length} files failed to upload.`,
    )
    this.name = 'PartialUploadError'
    this.result = result
  }
}

export interface RetryOptions {
  maxAttempts?: number
  baseDelayMs?: number
  maxDelayMs?: number
  exponentialBase?: number
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

const calculateDelay = (
  attempt: number,
  baseDelayMs: number,
  maxDelayMs: number,
  exponentialBase: number,
): number => {
  const exponentialDelay = baseDelayMs * exponentialBase ** attempt
  const delay = clamp(exponentialDelay, 0, maxDelayMs)
  // Add jitter (random 0-25% of delay)
  const jitter = delay * 0.25 * random(true)
  return delay + jitter
}

const shouldRetryError = (error: unknown): boolean => {
  if (
    error instanceof SweetspotError &&
    error.cause &&
    typeof error.cause === 'object' &&
    'status' in error.cause
  ) {
    const status = error.cause.status as number
    // Don't retry on 4xx errors except 429 (rate limit)
    return !(status >= 400 && status < 500 && status !== 429)
  }
  return true
}

export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> => {
  const {
    maxAttempts = 3,
    baseDelayMs = 1000,
    maxDelayMs = 10000,
    exponentialBase = 2,
  } = options

  let lastError: Error | unknown

  for (const attempt of times(maxAttempts)) {
    try {
      return await fn()
    } catch (error) {
      lastError = error

      if (!shouldRetryError(error)) {
        throw error
      }

      // If this is the last attempt, throw the error
      if (attempt === maxAttempts - 1) {
        throw error
      }

      // Wait before retrying
      const delay = calculateDelay(
        attempt,
        baseDelayMs,
        maxDelayMs,
        exponentialBase,
      )
      await sleep(delay)
    }
  }

  throw lastError
}

/**
 * Hex SHA-256 of a blob, computed client-side so it can be signed into the upload URL and
 * enforced by S3 at PUT time. Undefined wherever WebCrypto isn't available (an old browser,
 * a non-secure context) — the server falls back to hashing the object itself when an upload
 * wasn't signed with one.
 */
export async function computeSha256Hex(
  blob: Blob,
): Promise<string | undefined> {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    return undefined
  }
  try {
    const digest = await crypto.subtle.digest(
      'SHA-256',
      await blob.arrayBuffer(),
    )
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return undefined
  }
}

/**
 * Uploads a file to S3 using a presigned URL.
 * This is a generic function that works for both organization and pursuit uploads.
 */
export async function putToS3(upload: UploadUrlOut, file: Blob): Promise<void> {
  // Use server-provided headers exactly (e.g., Content-Type)
  const headers = new Headers()
  for (const [k, v] of Object.entries(upload.headers || {})) {
    headers.set(k, v)
  }
  // If server did not specify Content-Type, fall back to file.type
  if (!headers.has('Content-Type') && file.type) {
    headers.set('Content-Type', file.type)
  }
  const res = await fetch(upload.url, {
    method: 'PUT',
    body: file,
    headers,
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'S3_PUT_ERROR',
      message: res.statusText,
      cause: { status: res.status },
    })
  }
}

export interface UploadFilesPresignedOptions<TCompleteParams> {
  basePath: string
  entityIdName: string
  buildBatchCompleteBody: (s3Keys: string[], params: TCompleteParams) => unknown
  useRetry?: boolean
  retryOptions?: RetryOptions
  progressAfterComplete?: boolean
}

export interface UploadFilesPresignedRunOptions {
  rejectOnPartialFailure?: boolean
}

export const createUploadFilesPresigned =
  <TCompleteParams>({
    basePath,
    entityIdName,
    buildBatchCompleteBody,
    useRetry = true,
    retryOptions,
    progressAfterComplete = true,
  }: UploadFilesPresignedOptions<TCompleteParams>) =>
  async (
    entityId: UUID | '',
    files: File[],
    accessToken: string | undefined | null,
    completeParams: TCompleteParams,
    batchSize: number,
    onProgress?: (progress: number) => void,
    { rejectOnPartialFailure = true }: UploadFilesPresignedRunOptions = {},
  ): Promise<UploadFilesPresignedResult> => {
    if (!entityId) {
      throw new Error(`No ${entityIdName} provided.`)
    }
    if (isNil(accessToken)) {
      throw new Error('No access token provided.')
    }

    const total = files.length
    let completedCount = 0

    // WebCrypto has no incremental digest, so hashing a file holds its whole
    // body in memory, and every file in a batch reaches this at once. They hash
    // one at a time; the uploads themselves still run the whole batch wide.
    let hashQueue: Promise<unknown> = Promise.resolve()
    const hashInTurn = (file: File): Promise<string | undefined> => {
      const hashed = hashQueue.then(() => computeSha256Hex(file))
      hashQueue = hashed.catch(() => undefined)
      return hashed
    }

    const uploadSingleFile = async (file: File): Promise<UploadUrlOut> => {
      // Hashed once up front rather than inside getUploadUrlFn, so a retried
      // presign request doesn't re-hash the file on every attempt.
      const contentSha256 = await hashInTurn(file)

      // Get upload URL
      const getUploadUrlFn = () => {
        const url = new URL(
          `${API_URL}${basePath}/${entityId}/files/upload-url`,
        )
        const params = new URLSearchParams()
        params.set('filename', file.name)
        if (file.type) {
          params.set('content_type', file.type)
        }
        if (contentSha256) {
          params.set('content_sha256', contentSha256)
        }
        url.search = params.toString()

        return fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }).then(async (res) => {
          if (!res.ok) {
            throw new SweetspotError({
              name: 'FETCH_ERROR',
              message: res.statusText,
              cause: { status: res.status },
            })
          }
          return (await res.json()) as UploadUrlOut
        })
      }

      const upload = useRetry
        ? await retryWithBackoff(getUploadUrlFn, retryOptions)
        : await getUploadUrlFn()

      // Upload to S3
      const putToS3Fn = () => putToS3(upload, file)
      if (useRetry) {
        await retryWithBackoff(putToS3Fn, retryOptions)
      } else {
        await putToS3Fn()
      }

      // Track progress after S3 upload if needed
      if (!progressAfterComplete) {
        const currentCount = ++completedCount
        if (!isNil(onProgress)) {
          onProgress(currentCount / total)
        }
      }
      return upload
    }

    const batches = chunk(files, batchSize)
    const createdFiles: UploadedFile[] = []
    const failedUploads: UploadFilesPresignedFailure[] = []

    for (const batch of batches) {
      const uploadResults = await Promise.allSettled(
        batch.map((file) => uploadSingleFile(file)),
      )

      const successfulUploads: Array<{ file: File; upload: UploadUrlOut }> = []
      for (const [index, result] of uploadResults.entries()) {
        const file = batch[index]
        if (result.status === 'fulfilled') {
          successfulUploads.push({ file, upload: result.value })
          continue
        }
        failedUploads.push({
          file,
          stage: 'upload',
          message:
            result.reason instanceof Error
              ? result.reason.message
              : String(result.reason),
          fileCreated: false,
        })
      }

      if (!successfulUploads.length) {
        if (progressAfterComplete && !isNil(onProgress)) {
          completedCount += batch.length
          onProgress(Math.min(1, completedCount / total))
        }
        continue
      }

      const completeFn = async () => {
        const res = await fetch(
          `${API_URL}${basePath}/${entityId}/files/complete-upload/batch`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(
              buildBatchCompleteBody(
                successfulUploads.map(({ upload }) => upload.s3_key),
                completeParams,
              ),
            ),
          },
        )
        if (!res.ok) {
          throw new SweetspotError({
            name: 'FETCH_ERROR',
            message: res.statusText,
            cause: { status: res.status },
          })
        }
        return (await res.json()) as CompleteUploadBatchResponse
      }

      const fileByS3Key = new Map(
        successfulUploads.map(({ file, upload }) => [upload.s3_key, file]),
      )
      let completedUploads: CompleteUploadBatchResponse
      try {
        completedUploads = useRetry
          ? await retryWithBackoff(completeFn, retryOptions)
          : await completeFn()
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        failedUploads.push(
          ...successfulUploads.map(({ file, upload }) => ({
            file,
            stage: 'complete' as const,
            message,
            s3Key: upload.s3_key,
            fileCreated: false,
          })),
        )
        if (progressAfterComplete) {
          completedCount += batch.length
          if (!isNil(onProgress)) {
            onProgress(Math.min(1, completedCount / total))
          }
        }
        continue
      }

      createdFiles.push(...completedUploads.created)
      failedUploads.push(
        ...completedUploads.errors.flatMap((error) => {
          const file = fileByS3Key.get(error.s3_key)
          if (isNil(file)) {
            return []
          }
          return [
            {
              file,
              stage: 'complete' as const,
              message: error.message,
              s3Key: error.s3_key,
              fileCreated: error.file_created,
            },
          ]
        }),
      )

      if (progressAfterComplete) {
        completedCount += batch.length
        if (!isNil(onProgress)) {
          onProgress(Math.min(1, completedCount / total))
        }
      }
    }

    const result = {
      created: createdFiles,
      failed: failedUploads,
    }
    if (rejectOnPartialFailure && failedUploads.length > 0) {
      throw new PartialUploadError(result)
    }
    return result
  }
